import { faCarAlt, faExternalLinkAlt, faMap } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useAssignmentGetUnassignedAssignmentsByAddressProximity,
  useGeocodeAssignmentCoordinatesHook,
} from 'api/assignment/assignment-proximity';
import {
  AssignmentModel,
  AssignmentViewModel,
  CaseModel,
  DrivingPolicyReportDto,
} from 'api/model';
import { useSetupGetBasicData } from 'api/setup/setup';
import TextButton from 'components/inputs/TextButton';
import Modal from 'components/Modal';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import useCompanies from 'contexts/basicData/useCompanies';
import useTranslations from 'contexts/basicData/useTranslations';
import useModalStack from 'contexts/modal/useModalStack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { openGoogleMapsDirections } from 'utils/address-helper';
import ReplacePlaceholderCarMap from './ReplacePlaceholderCarMap';
import {
  formatDateTime,
  getHoursAndMinutesFromMillisecondsString,
} from 'utils/date-helpers';
import {
  calculateHaversineDistance,
  formatDistance,
  postalCodeProximity,
} from 'utils/geo-distance';
import { makeNumberComparator, makeStringComparator } from 'utils/sorting';
import { useUserGetDrivingPolicyReportForUnPlannedAssignmentHook } from 'api/user/user';
import { formatAddress } from 'utils/address';
import ReplacePlaceholderAssignmentActionDropdown from './ReplacePlaceholderAssignmentActionDropdown';

const ModalTextButton = styled(TextButton)`
  text-align: left;
  padding: 0px;
`;

const StyledModal = styled(Modal)`
  & > div {
    max-height: 80vh;
    overflow: hidden;
  }

  & > div > .modal-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`;

const Header = styled.div`
  margin-bottom: 10px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const Span = styled.span`
  white-space: wrap;
`;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const TablePanel = styled.div`
  flex: 1;
  overflow: auto;
  min-width: 0;
`;

const MapPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const ToggleMapButton = styled(TextButton)`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const InvalidRowClassName = 'invalid-row';

const TableStylerWrapper = styled.div`
  display: contents;

  .${InvalidRowClassName} {
    color: ${({ theme }) => theme.colors.foreground.error};
  }
`;

const MissingCoordSpan = styled.span`
  color: ${({ theme }) => theme.colors.foreground.error};
  font-style: italic;
  font-size: 0.85em;
`;

const FetchLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.foreground.primary};
  cursor: pointer;
  font-size: 0.85em;
  text-decoration: underline;
  padding: 0;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
    text-decoration: none;
  }
`;

type AssignmentViewModelWithDrivingPolicyAndEstimatedEndTime = {
  drivingPolicy?: DrivingPolicyReportDto;
  assignment: AssignmentModel;
  case: CaseModel;
  estimatedEndTime?: Date;
};

const LARGE_DISTANCE = 999999999;

function hasCoordinates(
  lat: number | undefined | null,
  lon: number | undefined | null
): lat is number {
  return lat != null && lon != null;
}

function getStartDistance(
  current: AssignmentModel,
  candidate: AssignmentModel
): number | null {
  if (
    hasCoordinates(current.fromLatitude, current.fromLongitude) &&
    hasCoordinates(candidate.fromLatitude, candidate.fromLongitude)
  ) {
    return calculateHaversineDistance(
      current.fromLatitude!,
      current.fromLongitude!,
      candidate.fromLatitude!,
      candidate.fromLongitude!
    );
  }
  return null;
}

function getEndDistance(
  current: AssignmentModel,
  candidate: AssignmentModel
): number | null {
  if (
    hasCoordinates(current.toLatitude, current.toLongitude) &&
    hasCoordinates(candidate.toLatitude, candidate.toLongitude)
  ) {
    return calculateHaversineDistance(
      current.toLatitude!,
      current.toLongitude!,
      candidate.toLatitude!,
      candidate.toLongitude!
    );
  }
  return null;
}

function getCombinedSortValue(
  current: AssignmentModel,
  candidate: AssignmentModel
): number {
  const startDist = getStartDistance(current, candidate);
  const endDist = getEndDistance(current, candidate);

  if (startDist != null && endDist != null) {
    return startDist + endDist;
  }
  if (startDist != null) return startDist + LARGE_DISTANCE;
  if (endDist != null) return endDist + LARGE_DISTANCE;

  // Fallback to postal code proximity (inverted: more matching = lower value)
  const fromProx = postalCodeProximity(current.fromZip, candidate.fromZip);
  const toProx = postalCodeProximity(current.toZip, candidate.toZip);
  // Max 5 digits in Swedish postal codes, so max proximity = 5
  // Higher proximity = closer, so invert for sorting ascending
  return LARGE_DISTANCE * 2 + (10 - fromProx - toProx);
}

type ReplacePlaceholderCarButtonProps = {
  assignment: AssignmentViewModel;
  drivingPolicyReport: DrivingPolicyReportDto | undefined;
};

type ReplacePlaceholderCarModalProps = ReplacePlaceholderCarButtonProps & {
  onCancel: () => void;
};

const ReplacePlaceholderCarModal: React.FC<ReplacePlaceholderCarModalProps> = ({
  assignment,
  drivingPolicyReport,
  // onConfirm,
  onCancel,
}) => {
  const { activeCompaniesByAddrDict } = useCompanies();
  const basicdata = useSetupGetBasicData();
  const consecutiveHoursOfRestLast24Hours =
    basicdata.data?.consecutiveHoursOfRestLast24Hours ?? 0;
  const workHoursToday = drivingPolicyReport?.workHoursToday ?? 0;
  const { caseTypes, assignmentTypes } = useTranslations();
  const getUnassignedAssignmentsByProximityCall =
    useAssignmentGetUnassignedAssignmentsByAddressProximity();
  const geocodeAssignment = useGeocodeAssignmentCoordinatesHook();

  const refresh = async () => {
    await getUnassignedAssignmentsByProximityCall.mutateAsync({
      data: new Date(),
      placeholderAssignmentId: assignment.assignment.assignmentID,
    });
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isWorking, setIsWorking] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [hoveredAssignment, setHoveredAssignment] =
    useState<AssignmentModel | null>(null);
  const [hoveredRegNo, setHoveredRegNo] = useState<string>('');

  // Local copy of the current assignment with potentially geocoded coords
  const [patchedCurrentAssignment, setPatchedCurrentAssignment] = useState(
    assignment.assignment
  );

  // Track which candidate assignments are currently being geocoded
  const [geocodingIds, setGeocodingIds] = useState<Set<number>>(new Set());
  // Track which candidates failed geocoding (no coords found)
  const [failedGeocodingIds, setFailedGeocodingIds] = useState<Set<number>>(
    new Set()
  );

  const [assignmentsWithDrivingPolicy, setAssignmentsWithDrivingPolicy] =
    useState<AssignmentViewModelWithDrivingPolicyAndEstimatedEndTime[]>([]);

  // Auto-geocode current assignment if it's missing coordinates
  useEffect(() => {
    const a = assignment.assignment;
    const missingFrom = a.fromLatitude == null || a.fromLongitude == null;
    const missingTo = a.toLatitude == null || a.toLongitude == null;
    if (missingFrom || missingTo) {
      geocodeAssignment(a.assignmentID).then((result) => {
        if (result?.assignment) {
          setPatchedCurrentAssignment(result.assignment);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGeocodeCandidate = useCallback(
    async (assignmentId: number) => {
      setGeocodingIds((prev) => new Set(prev).add(assignmentId));
      try {
        const result = await geocodeAssignment(assignmentId);
        if (
          result?.assignment &&
          (result.assignment.fromLatitude != null ||
            result.assignment.toLatitude != null)
        ) {
          setAssignmentsWithDrivingPolicy((prev) =>
            prev.map((a) =>
              a.assignment.assignmentID === assignmentId
                ? { ...a, assignment: result.assignment, case: result.case }
                : a
            )
          );
        } else {
          setFailedGeocodingIds((prev) => new Set(prev).add(assignmentId));
        }
      } finally {
        setGeocodingIds((prev) => {
          const next = new Set(prev);
          next.delete(assignmentId);
          return next;
        });
      }
    },
    [geocodeAssignment]
  );

  const getDrivingPolicyForUplannedAssignmentCall =
    useUserGetDrivingPolicyReportForUnPlannedAssignmentHook();

  useEffect(() => {
    const fetchDrivingPolicy = async () => {
      setIsWorking(true);
      const assignedUserId = assignment.assignment.assignedTo?.userID;
      const data = getUnassignedAssignmentsByProximityCall.data ?? [];

      // Skip driving policy calls entirely if the current assignment has no assigned user
      const result: AssignmentViewModelWithDrivingPolicyAndEstimatedEndTime[] =
        !assignedUserId
          ? data.map((a) => ({ ...a }))
          : await Promise.all(
              data.map(async (a) => {
                if (
                  !a.assignment.estimatedDuration ||
                  !a.assignment.bookedTo ||
                  !a.assignment.estimatedStartTime
                ) {
                  return { ...a };
                }

                // as these are stopovers on their way home, we can base the times on the end time of our current assignment
                const usedStartTime = new Date(
                  assignment.assignment.estimatedStartTime!.getTime() +
                    assignment.assignment.estimatedDuration!
                );
                const estimatedDuration = a.assignment.estimatedDuration!;
                const usedEndTime = new Date(
                  usedStartTime.getTime() + estimatedDuration
                );
                const drivingPolicy =
                  await getDrivingPolicyForUplannedAssignmentCall({
                    userId: assignedUserId,
                    bookedTo: assignment.assignment.bookedTo,
                    startOrEstimatedStartTime: usedStartTime,
                    endOrEstimatedEndTime: usedEndTime,
                    estimatedDuration,
                  });

                return {
                  ...a,
                  drivingPolicy,
                  estimatedEndTime: usedEndTime,
                };
              })
            );
      setIsWorking(false);
      // Pre-sort by combined distance (start+end), then postal code proximity
      const currentAssignment = assignment.assignment;
      result.sort(
        (a, b) =>
          getCombinedSortValue(currentAssignment, a.assignment) -
          getCombinedSortValue(currentAssignment, b.assignment)
      );
      setAssignmentsWithDrivingPolicy(result);
    };

    if (getUnassignedAssignmentsByProximityCall.data) {
      fetchDrivingPolicy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUnassignedAssignmentsByProximityCall.data]);

  const goToCase = useCallback((caseID: number) => {
    // open the case in a new tab with the caseID
    window.open(`/sok/${caseID}`, caseID.toString());
  }, []);

  const getWorkTimeIncludingNewAssignment = useCallback(
    (_assignment: AssignmentModel) => {
      // get the current estimated duration in hours (default to 0)
      if (!_assignment.estimatedDuration) {
        return undefined;
      }
      const estimatedDuration = _assignment.estimatedDuration!;
      const alreadyUsedTime = workHoursToday * 60 * 60 * 1000;

      return estimatedDuration + alreadyUsedTime;
    },
    [workHoursToday]
  );

  const getTimeLeftIncludingNewAssignment = useCallback(
    (_assignment: AssignmentModel) => {
      // get the current estimated duration in hours (default to 0)
      if (!_assignment.estimatedDuration) {
        return undefined;
      }
      const estimatedDuration = _assignment.estimatedDuration!;
      const totalTimeAllowedToday =
        (24 - consecutiveHoursOfRestLast24Hours) * 60 * 60 * 1000;
      const alreadyUsedTime = workHoursToday * 60 * 60 * 1000;

      const timeLeftOfDay =
        totalTimeAllowedToday - estimatedDuration - alreadyUsedTime;

      return timeLeftOfDay;
    },
    [consecutiveHoursOfRestLast24Hours, workHoursToday]
  );

  const renderDistanceWithFetch = useCallback(
    (
      dist: number | null,
      candidateAssignmentId: number
    ): React.ReactNode => {
      if (dist != null) return formatDistance(dist);
      if (geocodingIds.has(candidateAssignmentId))
        return <MissingCoordSpan>Hämtar...</MissingCoordSpan>;
      if (failedGeocodingIds.has(candidateAssignmentId))
        return <MissingCoordSpan>Saknas</MissingCoordSpan>;
      return (
        <FetchLink
          onClick={(e) => {
            e.stopPropagation();
            handleGeocodeCandidate(candidateAssignmentId);
          }}
        >
          Hämta
        </FetchLink>
      );
    },
    [geocodingIds, failedGeocodingIds, handleGeocodeCandidate]
  );

  const columnSettings = useMemo((): ColumnSetting<
    AssignmentViewModelWithDrivingPolicyAndEstimatedEndTime,
    any
  >[] => {
    const currentAssignment = patchedCurrentAssignment;
    return [
      {
        head: 'Avstånd start',
        render: (a) =>
          renderDistanceWithFetch(
            getStartDistance(currentAssignment, a.assignment),
            a.assignment.assignmentID
          ),
        width: 120,
        sortFunction: makeNumberComparator((a) =>
          getStartDistance(currentAssignment, a.assignment) ?? LARGE_DISTANCE
        ),
      },
      {
        head: 'Avstånd slut',
        render: (a) =>
          renderDistanceWithFetch(
            getEndDistance(currentAssignment, a.assignment),
            a.assignment.assignmentID
          ),
        width: 120,
        sortFunction: makeNumberComparator((a) =>
          getEndDistance(currentAssignment, a.assignment) ?? LARGE_DISTANCE
        ),
      },
      {
        head: 'Skapat',
        render: (a) => formatDateTime(a.assignment.created),
        width: 130,
        sortFunction: makeNumberComparator(
          (a) => a.assignment.created?.getTime() ?? 0
        ),
      },
      {
        head: 'Regnr',
        render: (a) => a.case.registrationNumber,
        width: 100,
        sortFunction: makeStringComparator((a) => a.case.registrationNumber),
      },
      {
        head: 'Est. tid',
        render: (a) =>
          a.assignment.estimatedDuration
            ? getHoursAndMinutesFromMillisecondsString(
                a.assignment.estimatedDuration
              )
            : '–',
        width: 100,
        sortFunction: makeNumberComparator(
          (a) => a.assignment.estimatedDuration ?? 0
        ),
      },
      {
        head: 'Est. starttid',
        render: (a) =>
          a.assignment.estimatedStartTime
            ? formatDateTime(a.assignment.estimatedStartTime)
            : '–',
        width: 150,
        sortFunction: makeNumberComparator(
          (a) => a.assignment.estimatedStartTime?.getTime() ?? 0
        ),
      },
      {
        head: 'Est. sluttid',
        render: (a) =>
          a.estimatedEndTime ? formatDateTime(a.estimatedEndTime) : '–',
        width: 150,
        sortFunction: makeNumberComparator(
          (a) => a.estimatedEndTime?.getTime() ?? 0
        ),
      },
      {
        head: 'Bokad till',
        render: (a) =>
          a.assignment.bookedTo ? formatDateTime(a.assignment.bookedTo) : '–',
        width: 150,
        sortFunction: makeNumberComparator(
          (a) => a.assignment.bookedTo?.getTime() ?? 0
        ),
      },
      {
        head: 'Total tid inkl uppdraget',
        render: (a) => {
          const workTime = getWorkTimeIncludingNewAssignment(a.assignment);
          return workTime
            ? getHoursAndMinutesFromMillisecondsString(workTime)
            : '–';
        },
        width: 200,
        sortFunction: makeNumberComparator(
          (a) => getTimeLeftIncludingNewAssignment(a.assignment) ?? 0
        ),
      },
      {
        head: 'Tid kvar inkl uppdraget',
        render: (a) => {
          const timeLeft = getTimeLeftIncludingNewAssignment(a.assignment);
          return timeLeft
            ? getHoursAndMinutesFromMillisecondsString(timeLeft)
            : '-';
        },
        width: 200,
        sortFunction: makeNumberComparator(
          (a) => getTimeLeftIncludingNewAssignment(a.assignment) ?? 0
        ),
      },
      {
        head: 'Ärendetyp',
        render: (a) => caseTypes[a.case.caseTypeID],
        width: 100,
        sortFunction: makeStringComparator(
          (a) => caseTypes[a.case.caseTypeID] ?? ''
        ),
      },
      {
        head: 'Uppdragstyp',
        render: (a) => assignmentTypes[a.assignment.assignmentTypeID],
        width: 120,
        sortFunction: makeStringComparator(
          (a) => assignmentTypes[a.assignment.assignmentTypeID] ?? ''
        ),
      },
      {
        head: 'Från',
        render: (a) => {
          const addressKey =
            `${a.assignment.fromAddress}${a.assignment.fromZip}${a.assignment.fromCity}`
              .toLowerCase()
              .replaceAll(' ', '');
          const addressString =
            activeCompaniesByAddrDict.get(addressKey)?.name ??
            `${a.assignment.fromAddress}, ${a.assignment.fromZip}, ${a.assignment.fromCity}`;
          return <Span>{addressString}</Span>;
        },
        width: 200,
        sortFunction: makeStringComparator((a) => a.assignment.fromAddress),
      },
      {
        head: 'Till',
        render: (a) => {
          const addressKey =
            `${a.assignment.toAddress}${a.assignment.toZip}${a.assignment.toCity}`
              .toLowerCase()
              .replaceAll(' ', '');
          const addressString =
            activeCompaniesByAddrDict.get(addressKey)?.name ??
            `${a.assignment.toAddress}, ${a.assignment.toZip}, ${a.assignment.toCity}`;
          return <Span>{addressString}</Span>;
        },
        width: 200,
        sortFunction: makeStringComparator((a) => a.assignment.toAddress),
      },
      {
        head: '',
        render: (a) => (
          <TextButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openGoogleMapsDirections({
                fromAddress: currentAssignment.toAddress,
                fromCity: currentAssignment.toCity,
                fromZip: currentAssignment.toZip,
                toAddress: a.assignment.fromAddress,
                toCity: a.assignment.fromCity,
                toZip: a.assignment.fromZip,
              });
            }}
          >
            <img
              alt="Google maps icon"
              height={15}
              src="/images/googlemapsiconsmall.png"
              width={15}
            />
          </TextButton>
        ),
        width: 40,
      },
      {
        head: '',
        render: (a) => (
          <TextButton onClick={() => goToCase(a.case.caseID)}>
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </TextButton>
        ),
        width: 40,
      },
      {
        head: '',
        render: (a) => (
          <ReplacePlaceholderAssignmentActionDropdown
            currentAssignment={{
              assignment: assignment.assignment,
              case: assignment.case,
            }}
            selectedAssignment={{
              assignment: a.assignment,
              case: a.case,
            }}
          />
        ),
        width: 40,
      },
    ];
  }, [
    getWorkTimeIncludingNewAssignment,
    getTimeLeftIncludingNewAssignment,
    caseTypes,
    assignmentTypes,
    activeCompaniesByAddrDict,
    assignment.assignment,
    assignment.case,
    patchedCurrentAssignment,
    renderDistanceWithFetch,
    goToCase,
  ]);

  return (
    <StyledModal
      buttons={[
        {
          label: 'Stäng',
          onClick: onCancel,
        },
      ]}
      closeOnOutsideClick={false}
      onClose={onCancel}
      title="Välj bil för utfyllandsuppdraget"
    >
      <Header>
        Från:{' '}
        {formatAddress({
          name: assignment.assignment.fromName,
          address: assignment.assignment.fromAddress,
          zip: assignment.assignment.fromZip,
          city: assignment.assignment.fromCity,
          county: assignment.assignment.fromCounty,
        })}
        <ToggleMapButton onClick={() => setShowMap((v) => !v)}>
          <FontAwesomeIcon icon={faMap} />
          {showMap ? 'Dölj karta' : 'Visa karta'}
        </ToggleMapButton>
      </Header>
      <Header>
        Till:{' '}
        {formatAddress({
          name: assignment.assignment.toName,
          address: assignment.assignment.toAddress,
          zip: assignment.assignment.toZip,
          city: assignment.assignment.toCity,
          county: assignment.assignment.toCounty,
        })}
      </Header>
      <ContentWrapper>
        <TablePanel>
          {getUnassignedAssignmentsByProximityCall.isLoading || isWorking ? (
            <LoadingSpinner />
          ) : assignmentsWithDrivingPolicy?.length === 0 ? (
            <span>Inga uppdrag hittades</span>
          ) : (
            <TableStylerWrapper>
              <Table
                rows={assignmentsWithDrivingPolicy}
                columnSettings={columnSettings}
                onRowHover={
                  showMap
                    ? (row) => {
                        setHoveredAssignment(row.assignment);
                        setHoveredRegNo(row.case.registrationNumber ?? '');
                      }
                    : undefined
                }
                onRowLeave={
                  showMap
                    ? () => {
                        setHoveredAssignment(null);
                        setHoveredRegNo('');
                      }
                    : undefined
                }
                rowClassName={(row) => {
                  let rowClassName = '';
                  if (row.assignment.estimatedDuration === null) {
                    rowClassName = InvalidRowClassName;
                  }

                  const tooLongEstimatedDuration =
                    !row.assignment.estimatedDuration ||
                    !assignment.assignment.estimatedDuration ||
                    assignment.assignment.estimatedDuration <
                      row.assignment.estimatedDuration;
                  if (tooLongEstimatedDuration) {
                    rowClassName = InvalidRowClassName;
                  }
                  return `${rowClassName} ${row.assignment.assignmentID}`;
                }}
                initialSortState={{
                  ascending: true,
                  sortedColumnIndex: 0,
                }}
              />
            </TableStylerWrapper>
          )}
        </TablePanel>
        {showMap && (
          <MapPanel>
            <ReplacePlaceholderCarMap
              currentAssignment={patchedCurrentAssignment}
              hoveredAssignment={hoveredAssignment}
              hoveredRegistrationNumber={hoveredRegNo}
            />
          </MapPanel>
        )}
      </ContentWrapper>
    </StyledModal>
  );
};

const ReplacePlaceholderCarButton: React.FC<
  ReplacePlaceholderCarButtonProps
> = ({ assignment, drivingPolicyReport }) => {
  const modalStack = useModalStack();

  return (
    <ModalTextButton
      onClick={() => {
        modalStack.push(
          <ReplacePlaceholderCarModal
            assignment={assignment}
            drivingPolicyReport={drivingPolicyReport}
            onCancel={() => modalStack.pop()}
          />
        );
      }}
    >
      Välj bil <FontAwesomeIcon icon={faCarAlt} flip="horizontal" />
    </ModalTextButton>
  );
};

export default ReplacePlaceholderCarButton;
